import { useMemo } from "react";
import { TreeTableNode } from "../../../common/GroupedTreeTable";
import {
  ReportMetricDef,
  ReportTreeNodeV2,
  ReportV2Definition,
  requiredGroupOf,
  RunReportV2Response,
  ViewConfig,
} from "../../../../types/reportsV2";
import { BucketRange } from "../../../../utils/dateRangeFormat";
import {
  formatCells,
  levelLabelsOf,
  MetricIndex,
  metricIndexOf,
  sortReportNodes,
  toTreeTableNodes,
} from "../model/reportTree";

export interface ReportTableModel {
  /** Visible metric columns, in view order. */
  columns: ReportMetricDef[];
  /** The run's tree, sorted per the view (shared by table, CSV and print). */
  sortedNodes: ReportTreeNodeV2[];
  /** GroupedTreeTable nodes. Memoized: a new array resets the table's paging. */
  nodes: TreeTableNode[];
  totalsCells: Record<string, string>;
  index: MetricIndex;
  /** Grouping label per depth ("Date", "Staff name", …). */
  levelLabels: string[];
  /** "Date / Staff name / Line item type". */
  firstColumnLabel: string;
  range: BucketRange;
}

const EMPTY_INDEX: MetricIndex = new Map();
const EMPTY_RANGE: BucketRange = { rangeStart: "", rangeEnd: "" };

/**
 * Everything the table, export and print need from a run + the view. Labels
 * and grouping come from the RUN (what the data was computed for); columns
 * and sort come from the view (client-side, applied instantly).
 */
export function useReportTable(
  definition: ReportV2Definition,
  data: RunReportV2Response | null,
  view: ViewConfig,
  metricsByKey: ReadonlyMap<string, ReportMetricDef>,
): ReportTableModel {
  const columns = useMemo(
    () =>
      view.columns
        .map((key) => metricsByKey.get(key))
        .filter((m): m is ReportMetricDef => !!m),
    [view.columns, metricsByKey],
  );
  const index = useMemo(
    () => (data ? metricIndexOf(data) : EMPTY_INDEX),
    [data],
  );
  const range = useMemo(
    () =>
      data
        ? { rangeStart: data.date_range.start, rangeEnd: data.date_range.end }
        : EMPTY_RANGE,
    [data],
  );
  const sortedNodes = useMemo(
    () => (data ? sortReportNodes(data.nodes, view.sort, index) : []),
    [data, view.sort, index],
  );
  const nodes = useMemo(
    () =>
      data
        ? toTreeTableNodes(sortedNodes, {
            columns,
            index,
            granularity: data.granularity,
            range,
            groupCount: data.groups.length,
          })
        : [],
    [data, sortedNodes, columns, index, range],
  );
  const totalsCells = useMemo(
    () => (data ? formatCells(data.totals.values, { columns, index }) : {}),
    [data, columns, index],
  );
  const levelLabels = useMemo(() => {
    const granularity = data?.granularity ?? view.granularity;
    const groups = data?.groups ?? view.groups;
    return levelLabelsOf(
      requiredGroupOf(definition).label,
      definition.granularities.find((g) => g.key === granularity)?.label,
      granularity,
      groups.map(
        (key) => definition.group_bys.find((g) => g.key === key)?.label ?? key,
      ),
    );
  }, [data, view.granularity, view.groups, definition]);

  return {
    columns,
    sortedNodes,
    nodes,
    totalsCells,
    index,
    levelLabels,
    firstColumnLabel: levelLabels.join(" / "),
    range,
  };
}
